import Vector from "@images/background/vector.svg"
import { LogoHustleUpSmall } from "@images/logoHustleUpSmall"
import OnboardingLiteVersion from "@images/payment/onboardingLiteVersion.svg"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { OnboardingWrapper } from "@modules/owner/boxSettings/components/onboardingWrapper"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useNavigate } from "react-router-dom"
import styled, { useTheme } from "styled-components"

/**
 * This page is used for Stripe onboarding
 * so that the owners can create their Stripe account
 * @returns {React.ReactNode} - Wrapper & Header & BoxOnboardingStripeFirstPage | BoxOnboardingStripeSecondPage
 */
export const BoxOnboardingLiteVersion: React.FC = () => {
  const theme = useTheme()
  const { browserProfileService } = useServicesContext()
  const navigate = useNavigate()
  const boxNavigate = useBoxNavigate()
  const popup = useConfirmPopup()

  const handleClose = () =>
    popup.show({
      title: word("box.creation.form.cancel.title"),
      message: word("box.creation.form.cancel.description"),
      accept: async () => {
        browserProfileService.setNewTheme("athlete")
        setTimeout(() => navigate(Path.Root), 100)
      },
      footerProps: {
        align: true,
        confirmClassName: "flex-1",
        cancelClassName: "flex-1",
        footerClassName: "flex flex-row justify-content-around w-full",
      },
    })

  return (
    <OnboardingWrapper backgroundImage={Vector} handleClose={handleClose}>
      <div className="relative w-full flex flex-column align-content-between">
        {/* HEARDER */}
        <div className="flex align-items-center flex-column w-full mt-5 px-3 text-center">
          <div>
            <LogoHustleUpSmall color={theme.mainWhite} />
          </div>
          <HUText fontStyle="HL" color={theme.mainWhite} className="mt-3">
            {word("box.stripe.liteVersion.title")}
          </HUText>
          <HUText fontStyle="BL" color={theme.mainWhite} className="mt-1 flex">
            {word("box.stripe.liteVersion.subtitle")}
          </HUText>
        </div>
        {/* CONTENT */}
        <div className="flex flex-column mx-auto max-w-max">
          <div className="mt-5">
            <HUText fontStyle="HM" color={theme.mainWhite} className="flex mx-auto text-center px-4  w-10 sm:w-8">
              {word("box.stripe.liteVersion.content.title")}
            </HUText>
          </div>
          <div className="flex flex-column-reverse sm:flex-row w-full justify-content-center align-items-center mt-5 gap-3">
            <div className="w-9 sm:w-5">
              <HUText fontStyle="TS" color={theme.mainWhite}>
                {word("box.stripe.liteVersion.content.bulletPoints.title")}
              </HUText>
              <ul className="flex flex-column gap-3 mt-4 pl-4">
                {Array.from({ length: 5 }, (_, index) => (
                  <Li key={index}>
                    <HUText fontStyle="BM" color={theme.neutralColor700}>
                      {word(`box.stripe.liteVersion.content.bulletPoints.bullet${index + 1}` as keyof typeof word)}
                    </HUText>
                  </Li>
                ))}
              </ul>
              <div className="flex flex-columns gap-2 mb-4">
                <HUButton
                  type="Left_Icon"
                  size="M"
                  colorType="Quaternary"
                  text={word("global.previous")}
                  onClick={() => boxNavigate(Path.Box.OnboardingStripe)}
                  icon={{
                    iconView: <span className={`pi pi-arrow-left`} style={{ color: theme.mainWhite }} />,
                  }}
                  className="mt-4"
                />
                <HUButton
                  type="Rounded"
                  size="M"
                  colorType="Primary"
                  text={word("box.stripe.liteVersion.content.button")}
                  onClick={() => boxNavigate(Path.Root)}
                  className="mt-4"
                />
              </div>
            </div>
            <div
              className="w-full sm:w-5  h-10rem sm:h-22rem  bg-contain bg-no-repeat bg-center"
              style={{ backgroundImage: `url('${OnboardingLiteVersion} ')` }}
            ></div>
          </div>
        </div>
      </div>
    </OnboardingWrapper>
  )
}

const Li = styled.li`
  display: flex;
  align-items: center;
  position: relative;

  &::before {
    content: "•";
    color: ${({ theme }) => theme.primaryColor};
    font-size: 4em;
    position: absolute;
    left: -0.4em;
  }
`
